import { useEffect, useState } from "react";

interface UseMediaQueryOptions {
  // When true, the hook synchronously initializes from window.matchMedia on
  // first render. Use only when the consumer is gated behind a post-mount
  // boundary — otherwise SSR sees `false` and the client's first render sees
  // the real value, producing a hydration mismatch.
  lazyInit?: boolean;
}

const useMediaQuery = (query: string, options: UseMediaQueryOptions = {}): boolean => {
  const { lazyInit = false } = options;
  const [matches, setMatches] = useState(() =>
    lazyInit && typeof window !== "undefined" ? window.matchMedia(query).matches : false
  );

  useEffect(() => {
    const media = window.matchMedia(query);
    if (media.matches !== matches) {
      setMatches(media.matches);
    }
    const listener = () => setMatches(media.matches);
    window.addEventListener("resize", listener);
    return () => window.removeEventListener("resize", listener);
  }, [matches, query]);

  return matches;
};

export default useMediaQuery;
